import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  SubmitHandler,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormTrigger,
} from 'react-hook-form';
import { FormSubSteps, StepConfig } from './shared-toolbar-form-stepper';
import { Status } from './status-chip';
import SharedToolbarFormPanel from './shared-toolbar-form-panel';
import { stepHasError } from './shared-form-stepper';
import { ZodObject, ZodRawShape } from 'zod';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

export interface SharedToolbarFormPageProps<T extends FieldValues> {
  title: string;
  trigger: UseFormTrigger<T>;
  control: Control<T>;
  steps: StepConfig[];
  handleNext?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleDiscard?: () => void;
  onSubmit: SubmitHandler<T>;
  handleSubmit: UseFormHandleSubmit<T>;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  isDisableSubmit?: boolean;
  isLoadingSubmit: boolean;
  submitButtonText?: string;
  firstIdWithError?: string;
  setExpandedIds?: Dispatch<SetStateAction<Set<string>>>;
  errors: FieldErrors<T>;
  lastSavedDate?: number;
  hasBeenSaved?: boolean;
  handleSaveAndClose?: () => void;
  isSaving?: boolean;
  isFormDirty?: boolean;
  status?: Status;
  rightPanel?: React.ReactNode;
  subStep?: FormSubSteps[];
  stepIsLocked: boolean[];
  getValues: UseFormGetValues<T>;
  isLoadingData?: boolean;
  loadingSkeleton?: React.ReactNode;
  topContent?: (props: { onClose: () => void }) => React.ReactNode;
}

export default function SharedToolbarFormPage<T extends FieldValues>({
  title,
  trigger,
  control,
  getValues,
  subStep,
  steps,
  handleDiscard,
  handleSubmit,
  onSubmit,
  activeStep,
  setActiveStep,
  isDisableSubmit,
  isLoadingSubmit,
  submitButtonText,
  firstIdWithError,
  setExpandedIds,
  errors,
  lastSavedDate,
  handleSaveAndClose,
  isSaving,
  isFormDirty,
  status,
  rightPanel,
  stepIsLocked,
  isLoadingData,
  loadingSkeleton,
  topContent,
}: SharedToolbarFormPageProps<T>) {
  const theme = useTheme();
  const [showHelp, setShowHelp] = useState(false);
  const scrollToFirstError = (
    firstIdWithError?: string,
    setExpandedIds?: Dispatch<SetStateAction<Set<string>>>
  ) => {
    if (firstIdWithError && setExpandedIds) {
      setExpandedIds(new Set([firstIdWithError]));

      setTimeout(() => {
        const element = document.getElementById(firstIdWithError);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      const firstError = document.querySelector(
        '[aria-invalid="true"], .Mui-error input, .Mui-error textarea'
      ) as HTMLElement | null;
      requestAnimationFrame(() => {
        firstError?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        firstError?.focus();
      });
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !handleDiscard) {
        return;
      }

      if (event.defaultPrevented) {
        return;
      }
      const target = event.target as HTMLElement;
      const isDropdownOpen = target.getAttribute('aria-expanded') === 'true';
      const isInsideOverlay = !!target.closest(
        '.MuiDialog-root, .MuiPopover-root, .MuiMenu-root, [role="listbox"]'
      );

      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      )
        return;

      if (isDropdownOpen || isInsideOverlay) {
        return;
      }

      handleDiscard();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleDiscard]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit, () =>
        scrollToFirstError(firstIdWithError ?? undefined, setExpandedIds)
      )}
      sx={{ height: '100%', flex: rightPanel ? 79 : 100, minWidth: 0 }}
      noValidate
    >
      <SharedToolbarFormPanel
        title={title}
        lastSavedDate={lastSavedDate}
        isSaving={isSaving}
        status={status}
        onNeedHelp={topContent ? () => setShowHelp((prev) => !prev) : undefined}
        showHelp={showHelp}
        stepperProps={{
          activeStep,
          setActiveStep,
          steps,
          errors,
          trigger,
          stepIsLocked,
          subSteps: subStep,
          control,
          getValues,
        }}
        primaryButtons={
          <>
            <Button
              sx={{
                color: theme.palette.text.secondary,
                border: 'none',
                textTransform: 'none',
                fontWeight: 500,
              }}
              onClick={handleDiscard}
            >
              Cancel
            </Button>
            {handleSaveAndClose && (
              <Button
                variant="outlined"
                disabled={!isFormDirty}
                sx={{
                  color: theme.palette.semantic.button.secondary.text,
                  borderColor: theme.palette.semantic.button.secondary.border,
                  textTransform: 'none',
                }}
                onClick={handleSaveAndClose}
              >
                Save Draft
              </Button>
            )}
            <Button
              variant="contained"
              loading={isLoadingSubmit}
              disabled={isDisableSubmit ?? false}
              type="submit"
              sx={{ textTransform: 'none' }}
            >
              {submitButtonText ?? 'Finish'}
            </Button>
          </>
        }
      />
      {isLoadingData && loadingSkeleton ? (
        loadingSkeleton
      ) : (
        <Stack
          direction="row"
          sx={{ height: '100%', width: '100%', pt: '24px', gap: 3 }}
        >
          <Box
            sx={{
              height: '100%',
              flex: rightPanel ? 79 : 100,
              minWidth: 0,
              width: '100%',
            }}
          >
            <Grid container sx={{ minHeight: 0, width: '100%' }}>
              {/* Content Area */}
              <Grid
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  width: '100%',
                  minHeight: 0,
                  gap: '24px',
                }}
              >
                {topContent &&
                  showHelp &&
                  topContent({ onClose: () => setShowHelp(false) })}
                {steps.map((s, index) => {
                  const Icon = s.icon ?? undefined;
                  const isStepLocked = stepIsLocked[index];
                  const hasError =
                    s.schema &&
                    !isStepLocked &&
                    stepHasError(s.schema as ZodObject<ZodRawShape>, errors);

                  return (
                    <Card
                      onFocusCapture={() => {
                        if (activeStep !== index && !stepIsLocked[index]) {
                          setActiveStep(index);
                        }
                      }}
                      id={s.id}
                      key={s.id}
                      sx={{
                        width: '100%',
                        borderLeft: hasError
                          ? `4px solid ${theme.palette.semantic.outline.warning}`
                          : undefined,
                        borderRadius: '8px',
                        boxShadow: '0 1px 6px 0 rgba(0, 0, 0, 0.12)',
                        scrollMarginTop: '120px',
                      }}
                    >
                      <CardHeader
                        avatar={
                          <Box
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.semantic.surface.violet,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: 48,
                              height: 48,
                              borderRadius: 1,
                            }}
                          >
                            {Icon && (
                              <Icon
                                sx={{
                                  color: (theme) =>
                                    theme.palette.semantic.icon.violet,
                                }}
                              />
                            )}
                          </Box>
                        }
                        title={s.header}
                        subheader={s.subheader}
                        slotProps={{
                          title: {
                            variant: 'h6',
                            sx: {
                              fontWeight: 500,
                            },
                          },
                          subheader: {
                            variant: 'body2',
                            color: 'text.secondary',
                          },
                        }}
                        sx={{
                          borderBottom: (theme) =>
                            `1px solid ${theme.palette.semantic.outline.light}`,
                        }}
                      />
                      <CardContent sx={{ padding: '24px 36px' }}>
                        {s.component}
                      </CardContent>
                    </Card>
                  );
                })}
              </Grid>
            </Grid>
          </Box>
          {rightPanel && (
            <Box
              sx={{
                flex: 21,
                position: 'sticky',
                top: 110,
                alignSelf: 'flex-start',
                height: '100%',
              }}
            >
              {rightPanel}
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
